import { useEffect, useState } from "react";
import { Sparkles, ArrowRight } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

/**
 * Pre-Project Notice — shown right before the New-Project wizard for both
 * FlipCost and SpaceCost. Sets user expectations that built-in costs are
 * region averages, not quotes. The user must tick the acknowledgement
 * before they can continue.
 *
 * Reusable across both products via the `product` prop ("flipcost"|"spacecost"),
 * which only affects the heading wordmark and the data-theme accent.
 */
export default function PreProjectNoticeDialog({ open, onOpenChange, onContinue, product = "flipcost" }) {
  const [ack, setAck] = useState(false);
  const isSpaceCost = product === "spacecost";

  // Reset the checkbox each time the dialog opens so it doesn't carry state
  // across projects.
  useEffect(() => {
    if (open) setAck(false);
  }, [open]);

  const handleContinue = () => {
    if (!ack) return;
    onOpenChange(false);
    onContinue?.();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="pre-project-notice"
        data-theme={isSpaceCost ? "spacecost" : undefined}
        className="bg-white border-black/[0.08] text-[#27272A] sm:max-w-[560px]"
      >
        <DialogHeader>
          <div className="inline-flex items-center gap-1.5 self-start text-[10px] uppercase tracking-[0.2em] text-sage-700 bg-sage-100 border border-sage-200 rounded-full px-2.5 py-1 mb-2">
            <Sparkles className="w-3 h-3" />
            {isSpaceCost ? "SpaceCost · Pre-Project Notice" : "FlipCost · Pre-Project Notice"}
          </div>
          <DialogTitle className="fc-heading text-[#1C1C1C] text-xl">
            Plan smart. Price right.
          </DialogTitle>
          <DialogDescription className="text-sm text-[#57534E] leading-relaxed pt-1">
            {isSpaceCost ? "SpaceCost" : "FlipCost"} gives you a powerful starting point by using
            region-based average costs to map out your renovation and potential value. Every
            project is unique — costs can shift based on your location, finishes, suppliers, and
            build complexity. Ensure to:
          </DialogDescription>
        </DialogHeader>

        <ul className="space-y-2 text-sm text-[#27272A] py-1" data-testid="pre-project-notice-list">
          {[
            "Review and tailor costs to your project",
            "Adjust finishes and specifications where needed",
            "Get quotes from contractors before starting your project",
          ].map((line) => (
            <li key={line} className="flex items-start gap-2.5">
              <span className="mt-2 w-1.5 h-1.5 rounded-full bg-sage-700 shrink-0" />
              <span>{line}</span>
            </li>
          ))}
        </ul>

        <p className="text-sm text-[#57534E] leading-relaxed">
          This way, your plan stays realistic — and your decisions stay confident.
        </p>

        <div className="border-t border-black/[0.08] my-1" />

        <label
          className="flex items-start gap-3 cursor-pointer group select-none"
          data-testid="pre-project-notice-ack-label"
        >
          <input
            type="checkbox"
            data-testid="pre-project-notice-ack-checkbox"
            checked={ack}
            onChange={(e) => setAck(e.target.checked)}
            className="mt-0.5 w-4 h-4 accent-sage-700 cursor-pointer"
          />
          <span className="text-sm text-[#27272A] leading-snug">
            <strong className="font-medium">Got it</strong> — I'll review, adjust, and confirm
            costs with quotes before starting.
          </span>
        </label>

        <DialogFooter className="pt-2">
          <Button
            type="button"
            data-testid="pre-project-notice-continue-btn"
            disabled={!ack}
            onClick={handleContinue}
            className="bg-[#1C1C1C] text-white hover:bg-[#2A2A2E] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Continue <ArrowRight className="w-4 h-4 ml-1.5" />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
